import React, { useState, useEffect } from 'lib/react';
import {
  EVGrid, EVInputLabel, EVButton, EVSelect, EVMenuItem, EVTypography, EVIconButton, EVEditOutlined,
} from 'lib/components';
import { useTranslation } from 'lib/translation';
import clsx from 'lib/clsx';
import TextInputField from 'commonComponents/textInputField';
import {
  COUNTRY_US, ZIP_CODE_US_REGEX, ZIP_CODE_CA_REGEX, CANADA_PROVINCES, US_STATES,
} from 'constants/AppConstants';
import { formatAddress } from 'features/order/Order.utils';
import { useDispatch, useSelector } from 'lib/redux';
import AddressComponentStyles from './AddressComponentStyles';
import { updateAddressVerificationMapPoint } from '../AddressVerification.actions';

function AddressComponent() {
  const classes = AddressComponentStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { addressVerificationMapPoint } = useSelector(({ reportDetailsPage }) => reportDetailsPage);
  const [buildingDetails, setBuildingDetails] = useState({ ...addressVerificationMapPoint?.address, building: addressVerificationMapPoint?.building });
  useEffect(
    () => setBuildingDetails({ ...addressVerificationMapPoint?.address, building: addressVerificationMapPoint?.building }),
    [addressVerificationMapPoint],
  );
  const inputFieldStyles = {
    FormHelperTextProps: { classes: { error: classes.inputHelperText } },
    InputProps: { classes: { root: classes.inputTextField } },
  };
  const [zipError, setZipError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const country = 'US';
  const statesList = country === COUNTRY_US ? US_STATES : CANADA_PROVINCES;
  const handleZipCodeValidation = (value) => {
    const zipCodeRegex = country === COUNTRY_US ? ZIP_CODE_US_REGEX : ZIP_CODE_CA_REGEX;
    return zipCodeRegex.test(value);
  };
  const handleChange = (event) => {
    const { target: { id, value } } = event;
    setZipError('');
    setBuildingDetails({ ...buildingDetails, [id]: value });
  };

  const handleSelectOptionChange = (event) => {
    const { value } = event.target;
    setBuildingDetails({ ...buildingDetails, state: value });
  };

  const handleUpdateButton = (e) => {
    if (e) e.preventDefault();
    const zipCode = buildingDetails.zip;
    if (zipCode && !handleZipCodeValidation(zipCode)) {
      setZipError(t('order__valid_zip_code'));
    } else {
      const { building: updatedBuildingId, ...address } = buildingDetails;
      dispatch(updateAddressVerificationMapPoint({
        building: updatedBuildingId,
        address: { ...address },
      }));
      setIsEditing(false);
    }
  };
  const handleCancelClick = () => {
    setBuildingDetails({ ...addressVerificationMapPoint?.address, building: addressVerificationMapPoint?.building });
    setIsEditing(false);
  };
  const handleEditClick = () => setIsEditing(true);
  const isDisabled = !(buildingDetails.zip && buildingDetails.city && buildingDetails.address) || zipError;
  return (
    <EVGrid
      container
      className={classes.buildingDetailsContainer}
      component="form"
      onSubmit={handleUpdateButton}
    >
      {!isEditing ? (
        <EVGrid container className={classes.addressDetailsContainer}>
          <EVGrid data-testid="address-text" item style={{ display: 'flex', gap: '2px' }}>
            <EVTypography className={classes.reportAddressText}>
              <b>{t('address_verification__map__address')}</b>
              {': '}
              {formatAddress(buildingDetails)}
            </EVTypography>
          </EVGrid>
          <EVIconButton data-testid="edit-icon" className={classes.editOutlineButton} onClick={() => handleEditClick()}>
            <EVEditOutlined htmlColor="#009AC5" />
          </EVIconButton>
        </EVGrid>
      )
        : (
          <>
            <EVGrid className={classes.editAddressInputContainer}>
              <EVTypography style={{ fontWeight: 600, fontSize: '16px' }}>
                {`${t('address_verification__map__address')}: `}
              </EVTypography>
              <EVGrid container wrap="nowrap" className={classes.editAddressRow1}>
                <EVGrid item style={{ width: '100%' }}>
                  <TextInputField
                    handleChange={handleChange}
                    inputId="building"
                    inputLabelProps={{ className: classes.editBuildingLabel }}
                    inputValue={buildingDetails.building}
                    labelText={t('BuildingNameId')}
                    inputFieldProps={{ inputProps: { maxLength: 50 }, ...inputFieldStyles }}
                  />
                </EVGrid>
                <EVGrid item style={{ width: '100%' }}>
                  <TextInputField
                    handleChange={handleChange}
                    inputId="address"
                    inputLabelProps={{ className: classes.editBuildingLabel }}
                    inputValue={buildingDetails.address}
                    isRequired
                    labelText={t('BuildingAddress')}
                    inputFieldProps={inputFieldStyles}
                  />
                </EVGrid>
              </EVGrid>
              <EVGrid container wrap="nowrap" className={classes.editAddressRow2}>
                <EVGrid style={{ flex: '1 1 auto' }}>
                  <TextInputField
                    handleChange={handleChange}
                    inputId="city"
                    inputLabelProps={{ className: classes.editBuildingLabel }}
                    inputValue={buildingDetails.city}
                    isRequired
                    labelText={t('City')}
                    inputFieldProps={inputFieldStyles}
                  />
                </EVGrid>
                <EVGrid>
                  <EVInputLabel className={classes.editBuildingLabel} id="state">
                    {t('State')}
                  </EVInputLabel>
                  <EVSelect
                    variant="outlined"
                    id="state"
                    value={buildingDetails.state}
                    onChange={handleSelectOptionChange}
                    inputProps={{ classes: { root: clsx(classes.inputTextField, 'select') } }}
                  >
                    {statesList.map(({ abbreviation }) => (
                      // #genai - state abbreviation is the stable identity for each option
                      <EVMenuItem key={abbreviation} value={abbreviation}>
                        <em style={{ fontStyle: 'normal' }}>{abbreviation}</em>
                      </EVMenuItem>
                    ))}

                  </EVSelect>
                </EVGrid>
                <EVGrid style={{ width: '80px' }}>
                  <TextInputField
                    handleChange={handleChange}
                    inputId="zip"
                    inputLabelProps={{ className: clsx(classes.editBuildingLabel, 'postalcode') }}
                    inputValue={buildingDetails.zip}
                    isRequired
                    labelText={t('ZipCode')}
                    inputFieldProps={{ inputProps: { maxLength: 10 }, ...inputFieldStyles }}
                    helperText={zipError}
                  />
                </EVGrid>
              </EVGrid>
            </EVGrid>
            <EVGrid className={classes.editComponentButtonRow}>
              <EVButton
                id="saveEditBtn"
                disabled={isDisabled}
                variant="contained"
                color="primary"
                type="submit"
              >
                {t('Save')}
              </EVButton>
              <EVButton
                id="cancelEditBtn"
                onClick={handleCancelClick}
                variant="outlined"
                color="primary"
              >
                {t('common_cancel')}
              </EVButton>
            </EVGrid>
          </>
        )}
    </EVGrid>
  );
}

export default AddressComponent;
